import { useEffect, useMemo, useState } from 'react';
import { useProducts, useStockUnitGroups, useStockUnits } from '../api/hooks';
import type { Product, StockUnit, StockUnitGroup } from '../api/types';
import { I } from '../components/Icons';
import { Btn, Search } from '../components/ui';
import { czk, dt } from '../lib/format';

/**
 * Výběr ze skladu pro jednu položku dokladu (kolečko + nalevo od položky).
 * Telefony jsou po skupinách zboží + varianta + stav („iPhone 16 Pro 128GB · stav A · 34 ks“);
 * u skupiny se zadá počet (třeba 20) a vyberou se kusy od nejstarších – každý s vlastním IMEI.
 * Výběr jde upravit odškrtnutím konkrétních kusů. Příslušenství (bez IMEI) jen počtem.
 * Nákupní ceny jsou jen informace – na doklad se nedostanou.
 */

export type StockPick =
  | { kind: 'units'; group: StockUnitGroup; units: StockUnit[] }
  | { kind: 'quantity'; product: Product; quantity: number };

export interface StockPickInitial {
  productId: string;
  variantId: string | null;
  grade: string;
  unitIds: string[];
}

export const groupTitle = (g: Pick<StockUnitGroup, 'productName' | 'variantName'>) =>
  [g.productName, g.variantName].filter(Boolean).join(' ');

/** Popisek položky ze skupiny: „Černá · stav A“. */
export const groupDescription = (g: Pick<StockUnitGroup, 'variantName' | 'grade'>) =>
  [g.variantName, g.grade ? `stav ${g.grade}` : ''].filter(Boolean).join(' · ');

const sameGroup = (g: StockUnitGroup, i: StockPickInitial) =>
  g.productId === i.productId && g.variantId === i.variantId && g.grade === i.grade;

export function StockPickSheet({
  companyId,
  initial,
  exclude,
  onDone,
  onCancel,
}: {
  companyId: string;
  /** úprava položky, která už kusy má: rovnou její skupina s vybranými kusy */
  initial?: StockPickInitial;
  /** kusy už použité v jiných položkách dokladu */
  exclude: Set<string>;
  onDone: (pick: StockPick) => void;
  onCancel: () => void;
}) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const [group, setGroup] = useState<StockUnitGroup | null>(null);
  const [product, setProduct] = useState<Product | null>(null);
  const [triedInitial, setTriedInitial] = useState(!initial);
  const groupsQ = useStockUnitGroups(companyId, debounced);
  const groups = groupsQ.data?.groups ?? [];
  const { data: products = [] } = useProducts(companyId, debounced, true);
  const accessories = products.filter((p) => !p.tracksSerial && Number(p.quantity) > 0);

  // úprava položky: jakmile dorazí skupiny, otevřít tu její
  useEffect(() => {
    if (triedInitial || !groupsQ.data || !initial) return;
    const g = groupsQ.data.groups.find((x) => sameGroup(x, initial));
    if (g) setGroup(g);
    setTriedInitial(true);
  }, [triedInitial, groupsQ.data, initial]);

  if (group) {
    return (
      <GroupDetail
        companyId={companyId}
        group={group}
        initialIds={initial && sameGroup(group, initial) ? initial.unitIds : []}
        exclude={exclude}
        onBack={() => setGroup(null)}
        onDone={(units) => onDone({ kind: 'units', group, units })}
      />
    );
  }
  if (product) {
    return (
      <QuantityDetail
        product={product}
        onBack={() => setProduct(null)}
        onDone={(quantity) => onDone({ kind: 'quantity', product, quantity })}
      />
    );
  }

  return (
    <>
      <h3>Ze skladu</h3>
      <p className="muted mb">
        Vyber zboží a počet kusů. Nákupní cena je jen pro tebe – na doklad se nedostane.
      </p>
      <Search value={q} onChange={setQ} placeholder="Název zboží nebo IMEI" />
      <div className="qp-list">
        {groups.map((g) => (
          <button
            type="button"
            key={`${g.productId}:${g.variantId ?? ''}:${g.grade}`}
            className="qp-row"
            aria-label={`${groupTitle(g)}${g.grade ? `, stav ${g.grade}` : ''}, skladem ${g.count} ks`}
            onClick={() => setGroup(g)}
          >
            <span className="grow">
              <span className="l1">{groupTitle(g)}</span>
              <span className="l2">
                {g.grade && <span className="grade">stav {g.grade}</span>}
                <span>skladem {g.count} ks</span>
                <span className="price-tag">
                  nákup {g.minCost === g.maxCost ? czk(g.avgCost) : `Ø ${czk(g.avgCost)}`}
                </span>
                {g.salePrice && <span>prodej {czk(g.salePrice)}</span>}
              </span>
            </span>
            <span className="qp-plus" aria-hidden="true">
              {I.plus}
            </span>
          </button>
        ))}
        {accessories.map((p) => (
          <button
            type="button"
            key={p.id}
            className="qp-row"
            aria-label={`${p.name}, skladem ${p.quantity} ${p.unit}`}
            onClick={() => setProduct(p)}
          >
            <span className="grow">
              <span className="l1">{p.name}</span>
              <span className="l2">
                <span>
                  skladem {p.quantity} {p.unit}
                </span>
                {p.purchasePrice && <span className="price-tag">nákup {czk(p.purchasePrice)}</span>}
                {p.salePrice && <span>prodej {czk(p.salePrice)}</span>}
              </span>
            </span>
            <span className="qp-plus" aria-hidden="true">
              {I.plus}
            </span>
          </button>
        ))}
        {!groups.length && !accessories.length && (
          <div className="empty">
            {groupsQ.isPending
              ? 'Načítám…'
              : debounced
                ? 'Skladem nic neodpovídá hledání.'
                : 'Sklad je prázdný – naskladni zboží ve Skladu.'}
          </div>
        )}
      </div>
      <Btn full className="mt" onClick={onCancel}>
        Zavřít
      </Btn>
    </>
  );
}

/** Počet kusů ze skupiny: stepper bere kusy od nejstarších, jednotlivé jde odškrtnout. */
function GroupDetail({
  companyId,
  group,
  initialIds,
  exclude,
  onBack,
  onDone,
}: {
  companyId: string;
  group: StockUnitGroup;
  initialIds: string[];
  exclude: Set<string>;
  onBack: () => void;
  onDone: (units: StockUnit[]) => void;
}) {
  const unitsQ = useStockUnits({
    companyId,
    productId: group.productId,
    grade: group.grade,
    variantId: group.variantId ?? 'none',
    status: 'in_stock',
    sort: 'fifo',
  });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = unitsQ;
  const avail = useMemo(
    () => unitsQ.data.filter((u) => !exclude.has(u.id)),
    [unitsQ.data, exclude],
  );
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initialIds));
  // požadovaný počet ze stepperu / pole; výběr se k němu doplní, až se kusy načtou
  const [target, setTarget] = useState<number | null>(initialIds.length ? null : 1);
  const [typed, setTyped] = useState('');
  const max = hasNextPage ? group.count : avail.length;

  const pending = unitsQ.isPending;
  useEffect(() => {
    // počet se doplní až nad načtenými kusy (jinak by se požadavek „1 kus“ ztratil)
    if (target == null || pending) return;
    const want = Math.max(0, Math.min(target, max));
    if (avail.length < want && hasNextPage) {
      if (!isFetchingNextPage) fetchNextPage();
      return;
    }
    setSelected((old) => {
      const cur = avail.filter((u) => old.has(u.id)).map((u) => u.id);
      if (cur.length === want) return old;
      if (cur.length > want) return new Set(cur.slice(0, want));
      const next = new Set(cur);
      for (const u of avail) {
        if (next.size >= want) break;
        next.add(u.id);
      }
      return next;
    });
    setTarget(null);
  }, [target, pending, max, avail, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const count = avail.filter((u) => selected.has(u.id)).length;
  const toggle = (id: string) =>
    setSelected((old) => {
      const next = new Set(old);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const picked = avail.filter((u) => selected.has(u.id));
  const cost = picked.reduce((a, u) => a + (Number(u.purchasePrice) || 0), 0);
  const loading = target != null || pending;

  return (
    <>
      <button type="button" className="link" onClick={onBack}>
        ‹ Zpět na sklad
      </button>
      <h3 className="mt">{groupTitle(group)}</h3>
      <p className="muted mb">
        {group.grade ? `stav ${group.grade} · ` : ''}skladem {group.count} ks · zadej počet, kusy se
        vezmou od nejstarších
      </p>
      <fieldset className="qty-stepper" aria-label="Počet kusů">
        <button
          type="button"
          aria-label="O kus méně"
          disabled={count <= 0}
          onClick={() => setTarget(count - 1)}
        >
          −
        </button>
        <input
          aria-label="Počet kusů"
          inputMode="numeric"
          value={typed !== '' ? typed : String(count)}
          onChange={(e) => setTyped(e.target.value.replace(/\D/g, ''))}
          onBlur={() => {
            if (typed !== '') setTarget(Number(typed));
            setTyped('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
        />
        <button
          type="button"
          aria-label="O kus více"
          disabled={count >= max}
          onClick={() => setTarget(count + 1)}
        >
          +
        </button>
        <button type="button" className="link" onClick={() => setTarget(max)}>
          Vše ({max})
        </button>
      </fieldset>
      <div className="stock-foot">
        <Btn variant="primary" full disabled={!count || loading} onClick={() => onDone(picked)}>
          Vložit {count} ks
        </Btn>
        {count > 0 && (
          <span className="price-tag">
            nákup celkem {czk(cost)} · jen pro tebe, na doklad se netiskne
          </span>
        )}
      </div>
      <div className="qp-h mt">IMEI kusů – od nejstarších, klepnutím přidáš nebo odebereš</div>
      <div className="qp-list">
        {avail.map((u) => {
          const on = selected.has(u.id);
          return (
            <button
              type="button"
              key={u.id}
              className={`qp-row${on ? ' on' : ''}`}
              aria-pressed={on}
              aria-label={`IMEI ${u.serial}`}
              onClick={() => toggle(u.id)}
            >
              <span className="grow">
                <span className="l1 mono">{u.serial}</span>
                <span className="l2">
                  <span>naskladněno {dt(u.purchasedAt)}</span>
                  <span className="price-tag">nákup {czk(u.purchasePrice)}</span>
                </span>
              </span>
              <span className={`qp-check${on ? ' on' : ''}`} aria-hidden="true">
                {on ? '✓' : ''}
              </span>
            </button>
          );
        })}
        {!avail.length && <div className="empty">{loading ? 'Načítám…' : 'Žádný volný kus.'}</div>}
      </div>
      {hasNextPage && (
        <button
          type="button"
          className="link qp-more"
          disabled={isFetchingNextPage}
          onClick={() => fetchNextPage()}
        >
          Načíst další kusy
        </button>
      )}
    </>
  );
}

/** Příslušenství bez IMEI: jen počet (nejvýš co je skladem). */
function QuantityDetail({
  product,
  onBack,
  onDone,
}: {
  product: Product;
  onBack: () => void;
  onDone: (quantity: number) => void;
}) {
  const max = Number(product.quantity) || 0;
  const [n, setN] = useState(1);
  const clamp = (v: number) => Math.max(1, Math.min(max, v));
  return (
    <>
      <button type="button" className="link" onClick={onBack}>
        ‹ Zpět na sklad
      </button>
      <h3 className="mt">{product.name}</h3>
      <p className="muted mb">
        skladem {product.quantity} {product.unit}
        {product.purchasePrice ? ` · nákup ${czk(product.purchasePrice)}` : ''}
      </p>
      <fieldset className="qty-stepper" aria-label="Počet kusů">
        <button
          type="button"
          aria-label="O kus méně"
          disabled={n <= 1}
          onClick={() => setN(clamp(n - 1))}
        >
          −
        </button>
        <input
          aria-label="Počet kusů"
          inputMode="numeric"
          value={String(n)}
          onChange={(e) => setN(clamp(Number(e.target.value.replace(/\D/g, '')) || 1))}
        />
        <button
          type="button"
          aria-label="O kus více"
          disabled={n >= max}
          onClick={() => setN(clamp(n + 1))}
        >
          +
        </button>
      </fieldset>
      <Btn variant="primary" full className="mt" disabled={max < 1} onClick={() => onDone(n)}>
        Vložit {n} {product.unit}
      </Btn>
    </>
  );
}
